"use client";

/**
 * DEV-ONLY, tree-shaken from prod by NODE_ENV === "production" early return.
 * Pairs with an uncommitted speak-server endpoint at /api/dev/cohorts +
 * /api/dev/swap-cohort. No passwords or emails exist on the client.
 */

import { useEffect, useRef, useState } from "react";
import { FlaskConical } from "lucide-react";
import { Button } from "@speakai/ui/button";
import { Badge } from "@speakai/ui/badge";
import { cn } from "@/lib/utils/cn";
import { useToast } from "@speakai/ui/toast";
import { useAuth } from "@/hooks/use-auth";
import { apiClient } from "@/lib/api/client";
import type { LoginResponse } from "@/features/auth/api";
import { clearLegacyAuthStorage } from "@/lib/auth/storage";
import { STORAGE_KEYS } from "@/config/constants";

type CohortListItem = {
  id: string;
  label: string;
  email: string;
  source: "seed" | "real";
  description?: string;
};

export function DevCohortSwitcher() {
  // Hooks must be called unconditionally (Rules of Hooks), gate only
  // the render output and the fetch body, not the hook calls.
  const { loginWithTokens, user } = useAuth();
  const { error: toastError } = useToast();
  const [cohorts, setCohorts] = useState<CohortListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  const enabled =
    !import.meta.env.PROD &&
    import.meta.env.NEXT_PUBLIC_DEV_COHORT_BAR === "1";

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    apiClient
      .get<CohortListItem[]>("/api/dev/cohorts")
      .then(res => {
        if (cancelled) return;
        if (res.success && res.data) {
          setCohorts(res.data);
        } else {
          // 404 lands here with success:false, dev-tools likely not enabled on server.
          console.warn(
            "[DevCohortSwitcher] /api/dev/cohorts returned no data, is ENABLE_DEV_COHORT=1 set on speak-server?",
            res.error
          );
        }
      })
      .catch(err => {
        // Network error (rare, 404s land in .then with success:false).
        console.warn("[DevCohortSwitcher] /api/dev/cohorts fetch failed", err);
      });
    return () => { cancelled = true; };
  }, [enabled]);

  // Close on click outside, mousedown so it fires before blur
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  if (!enabled) return null;

  const currentLabel = user?.email?.split("@")[0] ?? "cohort";

  const handleSwap = async (cohortId: string) => {
    setOpen(false);
    setBusy(true);
    try {
      const res = await apiClient.post<LoginResponse>("/api/dev/swap-cohort", { cohortId });
      if (!res.success || !res.data) {
        toastError(`Swap failed: ${res.error || "unknown"}`);
        return;
      }
      // Clear old cohort tokens first, then apply new ones. Order matters:
      // loadUserProfile (called inside loginWithTokens) uses localStorage
      // ACCESS_TOKEN as the dev session hint (no CSRF cookie in dev).
      clearLegacyAuthStorage();
      const { accessToken, refreshToken } = res.data;
      if (accessToken) {
        apiClient.setToken(accessToken);
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      }
      if (refreshToken) {
        apiClient.setRefreshToken(refreshToken);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }
      await loginWithTokens(res.data);
      window.location.href = "/home";
    } catch (e) {
      toastError(`Swap failed: ${e instanceof Error ? e.message : "unknown"}`);
    } finally {
      setBusy(false);
    }
  };

  if (cohorts.length === 0) return null;

  return (
    <div ref={wrapperRef} className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60]">
      {open && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-popover border border-border rounded-lg shadow-lg overflow-hidden"
          style={{ width: "min(32rem, calc(100vw - 2rem))" }}
        >
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Swap cohort account
          </div>
          <div className="border-t border-border" />
          <div className="p-2">
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by email, label, or description…"
              autoFocus
              className="w-full px-2 py-1.5 text-sm rounded border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="border-t border-border" />
          <div className="overflow-y-auto max-h-[60vh]">
            {cohorts
              .filter((c) => {
                if (!filter.trim()) return true;
                const q = filter.toLowerCase();
                return (
                  c.email.toLowerCase().includes(q) ||
                  c.label.toLowerCase().includes(q) ||
                  (c.description ?? "").toLowerCase().includes(q) ||
                  c.id.toLowerCase().includes(q)
                );
              })
              .map(c => (
              <button
                key={c.id}
                onClick={() => handleSwap(c.id)}
                disabled={busy}
                className={cn(
                  "w-full text-left px-3 py-2.5 flex flex-col gap-0.5",
                  "hover:bg-muted focus:bg-muted focus:outline-none",
                  "disabled:pointer-events-none disabled:opacity-50",
                  "transition-colors"
                )}
              >
                <span className="flex items-center justify-between w-full gap-2">
                  <span className="font-medium text-sm truncate text-popover-foreground">{c.label}</span>
                  <Badge variant={c.source === "real" ? "error" : "secondary"} size="sm">
                    {c.source}
                  </Badge>
                </span>
                <span className="text-xs text-muted-foreground truncate w-full">{c.email}</span>
                {c.description ? (
                  <span className="text-xs text-muted-foreground/80 truncate w-full">{c.description}</span>
                ) : null}
              </button>
            ))}
            {cohorts.length > 0 && cohorts.filter((c) => {
              if (!filter.trim()) return true;
              const q = filter.toLowerCase();
              return c.email.toLowerCase().includes(q) || c.label.toLowerCase().includes(q) || (c.description ?? "").toLowerCase().includes(q) || c.id.toLowerCase().includes(q);
            }).length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">No cohorts match &ldquo;{filter}&rdquo;</div>
            ) : null}
          </div>
        </div>
      )}
      <Button size="sm" variant="outline" disabled={busy} onClick={() => setOpen(o => !o)}>
        <FlaskConical className="size-4 mr-1" />
        Dev: {currentLabel}
        <Badge variant="warning" size="sm" className="ml-2">dev</Badge>
      </Button>
    </div>
  );
}
